import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import type {
  SubscriptionInterval,
  AdminUserSummary,
  AdminUserDetail,
  AdminMetrics,
  AppGrant,
  LicenseRequest,
  LicenseRequestStatus,
  PaidPlanId
} from "@acruxsoft/platform-shared";

export const checkoutInit = httpsCallable<
  { planId: string; interval: SubscriptionInterval },
  { approvalUrl: string; subscriptionId: string }
>(functions, "checkoutInit");

export const cancelSubscription = httpsCallable<
  Record<string, never>,
  { ok: true }
>(functions, "cancelSubscription");

export const linkWixContact = httpsCallable<
  { wixContactId: string },
  { ok: true }
>(functions, "linkWixContact");

export const mintRedirectToken = httpsCallable<
  { audience: string },
  { customToken: string }
>(functions, "mintRedirectToken");

export const claimTrial = httpsCallable<
  Record<string, never>,
  { ok: true; trialExpiresAt: number | null }
>(functions, "claimTrial");

export const requestAppLicense = httpsCallable<
  { appId: string; company: string; phone: string; message?: string },
  { ok: true; status: LicenseRequestStatus; createdAt: number }
>(functions, "requestAppLicense");

export const adminSetAppAccess = httpsCallable<
  { uid: string; appId: string; enabled: boolean; expiresAt?: number; note?: string | null },
  { ok: true; action: "grant_app" | "extend_app" | "revoke_app" | "noop"; grant?: AppGrant }
>(functions, "adminSetAppAccess");

export const adminListLicenseRequests = httpsCallable<
  { status?: LicenseRequestStatus },
  { requests: LicenseRequest[] }
>(functions, "adminListLicenseRequests");

export const adminDismissLicenseRequest = httpsCallable<
  { uid: string; appId: string },
  { ok: true; status: LicenseRequestStatus }
>(functions, "adminDismissLicenseRequest");

export const adminListUsers = httpsCallable<
  { search?: string; cursor?: string; limit?: number; sort?: "email" | "createdAt" | "trialExpiresAt" | "lastSignInAt" },
  { users: AdminUserSummary[]; nextCursor: string | null }
>(functions, "adminListUsers");

export const adminGetUser = httpsCallable<
  { uid: string },
  AdminUserDetail
>(functions, "adminGetUser");

export const adminAssignPlan = httpsCallable<
  { uid: string; planId: PaidPlanId; interval: "monthly" | "yearly"; expiresAt: number },
  { ok: true; subscriptionId: string; action: "granted" | "extended" }
>(functions, "adminAssignPlan");

export const adminCancelSubscription = httpsCallable<
  { subscriptionId: string },
  { ok: true }
>(functions, "adminCancelSubscription");

export const adminDeleteUser = httpsCallable<
  { uid: string },
  { ok: true }
>(functions, "adminDeleteUser");

export const adminSendOobCode = httpsCallable<
  { uid: string; type: "VERIFY_EMAIL" | "PASSWORD_RESET" },
  { ok: true }
>(functions, "adminSendOobCode");

export const adminGetMetrics = httpsCallable<
  Record<string, never>,
  AdminMetrics
>(functions, "adminGetMetrics");

export const updateUserProfile = httpsCallable<
  { displayName?: string | null; company?: string | null; phone?: string | null },
  { ok: true; displayName?: string | null; company?: string | null; phone?: string | null }
>(functions, "updateUserProfile");
